"use client";

import { useQuery } from "@apollo/client/react";

import CoinPriceChangePercentageFormatter from "@/components/shared/coin-price-change-percentage-formatter";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type DaoCoinStat,
  DaoCoinStatsDocument,
  DaoCoinStatsOrderBy,
} from "@/graphql/codegen/graphql";
import type { CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";
import { MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING } from "@/utils/constants";
import { getVisiblePrice } from "@/utils/currency";
import { cn } from "@/utils/lib";
import { getOrderByKey } from "@/utils/trade";

import { trendingTabs } from "../trade/trending-coins/constants";

export const UsernamePageHeaderCoinPrice = ({
  tokenDetails,
}: {
  tokenDetails: CoinPropertiesResponse;
}) => {
  const username = tokenDetails.coinProperties.Profile.Username;

  const { data, loading: loadingCoinPriceData } = useQuery(
    DaoCoinStatsDocument,
    {
      fetchPolicy: "network-only",
      variables: {
        orderBy: getOrderByKey(
          [{ id: trendingTabs[1].orderBy, desc: true }],
          DaoCoinStatsOrderBy,
          DaoCoinStatsOrderBy.VolumeUsdPast_24HDesc,
        ),
        filter: {
          coinUsername: {
            equalTo: username,
          },
        },
        first: 1,
        offset: 0,
      },
    },
  );

  const coinData =
    (data?.daoCoinStats?.nodes?.[0] as DaoCoinStat | undefined) ?? null;

  const priceChangePct = (coinData?.[
    trendingTabs[1].priceChangeAccessorKey as keyof DaoCoinStat
  ] ?? 0) as number;
  const tokenPriceUsd = tokenDetails?.coinPrice
    ? getVisiblePrice(tokenDetails.coinPrice)
    : 0;
  const trendingColor =
    priceChangePct >= 0 &&
    priceChangePct < MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING
      ? "text-muted"
      : priceChangePct >= MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING
        ? "text-emerald-400"
        : "text-red-600";

  const trendingBgColor =
    priceChangePct >= 0 &&
    priceChangePct < MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING
      ? "bg-muted/10"
      : priceChangePct >= MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING
        ? "bg-green-600/10"
        : "bg-red-600/10";

  return (
    <div className="mr-3">
      {loadingCoinPriceData ? (
        <Skeleton className="h-[30px] w-[65px]" />
      ) : (
        <div className={cn("flex gap-2 text-lg", trendingColor)}>
          <CoinPriceFormatter
            price={tokenPriceUsd}
            className="font-mono text-lg"
          />
          {(priceChangePct < 0 ||
            priceChangePct >=
              MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING) && (
            <div
              className={cn(
                "flex items-center rounded-full px-3 font-mono text-xs",
                trendingBgColor,
              )}
            >
              <CoinPriceChangePercentageFormatter
                price={priceChangePct}
                showArrow={true}
                className="gap-0 text-xs"
                arrowSize={12}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
